/**
 * TanStack Query hook for unified search (/search/all)
 *
 * Provides cache-first unified search across movies, TV, persons, users, lists.
 * - Viewer-scoped caching for personalization
 * - Short staleTime for fresh results on new searches
 * - Debounce handled by component (300ms recommended)
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

// ============================================
// TYPES (from Search_v2_Final.md spec)
// ============================================

export type EntityType = 'movie' | 'tv' | 'person' | 'user' | 'list';

export interface UnifiedSearchResult {
  entity_type: EntityType;
  id: string | number;
  title: string;
  subtitle: string;
  image_url: string | null;
  score: number;
  match: {
    title_highlight: string;
    subtitle_highlight?: string;
    reasons: string[];
    match_strength: number;
  };
  meta: Record<string, unknown>;
}

export interface DisambiguationOption {
  entity_type: EntityType;
  id: string | number;
  title: string;
  subtitle: string;
  image_url: string | null;
}

export interface SearchResponse {
  query: string;
  results: UnifiedSearchResult[];
  has_more: boolean;
  disambiguation?: {
    should_show: boolean;
    message: string;
    options: DisambiguationOption[];
  };
  debug?: {
    intent: string;
    signals: Record<string, unknown>;
  };
}

// ============================================
// HOOK
// ============================================

interface UseSearchAllOptions {
  /** Minimum query length to trigger search (default: 2) */
  minLength?: number;
  /** Result limit (default: 10) */
  limit?: number;
  /** Whether the query is enabled (default: true when query.length >= minLength) */
  enabled?: boolean;
}

/**
 * Hook to search across all entity types using /search/all
 *
 * @param query - Search query string
 * @param options - Hook options
 * @returns TanStack Query result with SearchResponse data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useSearchAll(debouncedQuery);
 * ```
 */
export function useSearchAll(query: string, options: UseSearchAllOptions = {}) {
  const { user, session } = useAuth();
  const token = session?.access_token;

  const { minLength = 2, limit = 10, enabled } = options;

  // Viewer bucket for cache key (personalization varies by viewer)
  const viewerBucket = user?.id ?? 'anon';

  // Only search if query meets minimum length
  const shouldSearch = query.length >= minLength;
  const isEnabled = enabled ?? shouldSearch;

  return useQuery({
    queryKey: queryKeys.search.all(viewerBucket, query),
    enabled: isEnabled && shouldSearch,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        q: query,
        limit: String(limit),
      });

      const response = await apiFetch<SearchResponse>(
        `/search/all?${params.toString()}`,
        {
          token, // Optional: enables personalization if logged in
          signal,
        }
      );

      return response;
    },
    // Short staleTime: search results (including user avatars) should be fresh
    staleTime: 5 * 1000, // 5 seconds
    // Keep previous data ONLY if it's for the same query (prevents stale flash)
    placeholderData: (previousData) => (previousData?.query === query ? previousData : undefined),
  });
}

/**
 * Helper to get the appropriate route for a search result.
 * Returns null if navigation is not possible (e.g., user without username).
 */
export function getResultRoute(result: UnifiedSearchResult): string | null {
  switch (result.entity_type) {
    case 'movie':
      return `/movie/${result.id}`;
    case 'tv':
      return `/tv/${result.id}`;
    case 'person':
      return `/person/${result.id}`;
    case 'user': {
      // User routes use /u/:username - require username to navigate
      const username = (result.meta as { username?: string })?.username;
      if (!username) {
        console.warn('[getResultRoute] User result missing username:', result.id);
        return null; // Cannot navigate without username
      }
      return `/u/${username}`;
    }
    case 'list':
      return `/list/${result.id}`;
    default:
      return '/';
  }
}

/**
 * Helper to get the icon/badge for each entity type
 */
export function getEntityTypeBadge(entityType: EntityType): {
  label: string;
  color: string;
} {
  switch (entityType) {
    case 'movie':
      return { label: 'Movie', color: 'bg-blue-600' };
    case 'tv':
      return { label: 'TV', color: 'bg-purple-600' };
    case 'person':
      return { label: 'Person', color: 'bg-green-600' };
    case 'user':
      return { label: 'User', color: 'bg-cyan-600' };
    case 'list':
      return { label: 'List', color: 'bg-orange-600' };
    default:
      return { label: '', color: 'bg-gray-600' };
  }
}
